import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type Environment = 'development' | 'production' | 'staging' | 'local';

interface EnvironmentStore {
  isLocalhost: boolean;
  isDevelopment: boolean;
  isProduction: boolean;
  environment: Environment;
  checkEnvironment: () => void;
}

export const useEnvironmentStore = create<EnvironmentStore>()(
  devtools(
    (set) => ({
      isLocalhost: false,
      isDevelopment: false,
      isProduction: false,
      environment: 'production' as Environment,
      
      checkEnvironment: () => {
        const hostname = window.location.hostname;
        const isLocal = hostname.includes('localhost') || hostname.includes('127.0.0.1');
        
        // Check if we're in development mode
        const isDev = process.env.NODE_ENV === 'development';
        const isProd = process.env.NODE_ENV === 'production';
        
        // Determine environment based on hostname and NODE_ENV
        let environment: Environment = 'production';
        if (isLocal) {
          environment = 'local';
        } else if (hostname.includes('staging') || hostname.includes('dev.')) {
          environment = 'staging';
        } else if (isDev && !isProd) {
          environment = 'development';
        }
        
        set({
          isLocalhost: isLocal,
          isDevelopment: isDev || environment === 'local',
          isProduction: isProd && environment === 'production',
          environment,
        });
      },
    }),
    {
      name: 'environment-store',
    }
  )
);

// Initialize on store creation
useEnvironmentStore.getState().checkEnvironment();

// Export convenience hooks
export const useIsLocalhost = () => useEnvironmentStore((state) => state.isLocalhost);
export const useIsDevelopment = () => useEnvironmentStore((state) => state.isDevelopment);
export const useIsProduction = () => useEnvironmentStore((state) => state.isProduction);
export const useEnvironment = () => useEnvironmentStore((state) => state.environment);

// Hook to get all environment info at once
export const useEnvironmentInfo = () => useEnvironmentStore((state) => ({
  isLocalhost: state.isLocalhost,
  isDevelopment: state.isDevelopment,
  isProduction: state.isProduction,
  environment: state.environment,
}));

// Development helper
if (process.env.NODE_ENV === 'development') {
  (window as any).environmentStore = useEnvironmentStore;
  (window as any).getEnvironmentInfo = () => useEnvironmentStore.getState();
}